import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { getThemeClassName } from 'lib/theme/themeUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import Image from 'components/Common/Image'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import SiteTakeoverCampaignModal from './SiteTakeoverCampaignModal'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import { useIntl } from 'react-intl'
import useResponsiveSelector from 'hooks/useResponsiveSelector'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  inset: 0;
  width: 100%;
`

const Content = styled(Group)`
  transform: translateZ(0);
`

const Banner = styled(Group)`
  padding: ${rem(8)} ${rem(12)};
  position: relative;
  min-height: ${rem(48)};
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  inset: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const IconWrapper = styled.span`
  display: flex;
  cursor: pointer;
  align-items: center;
`

const TitleSpacer = styled.span`
  margin-inline-end: ${rem(8)};
`

interface Props {
  vertical?: App.SiteTakeoverVertical
}
function SiteTakeoverOfferPageBanner({ vertical }: Props) {
  const intl = useIntl()
  const bannerRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(bannerRef, 'static')
  const customTakeover = useCurrentSiteTakeover({ vertical })
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const showModal = useContext(ModalContext)

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const handleModalOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    showModal(<SiteTakeoverCampaignModal />)
  }, [showModal])

  const useLab = !!siteTakeoverTheme && !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!customTakeover?.offerPage?.bannerContent

  const shouldHideOnMobile = useCustom && customTakeover?.offerPage?.hideOnMobileScreenSize

  // Extract values for lab config
  const isDesktop = useResponsiveSelector({
    mobile: false,
  }) ?? true
  const locationIcons = siteTakeoverBannerContentPayload?.locationIcons
  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const locationIcon = locationIcons?.offer
    if (locationIcon) {
      const icon = isDesktop ? locationIcon.desktopIcon : locationIcon.mobileIcon
      if (icon) {
        const iconWidth = isDesktop ? locationIcon.desktopIconDimension?.width : locationIcon.mobileIconDimension?.width
        const iconHeight = isDesktop ? locationIcon.desktopIconDimension?.height : locationIcon.mobileIconDimension?.height
        return {
          id: icon,
          width: iconWidth,
          height: iconHeight,
        }
      }
    }
    const fallbackLogoRef = siteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [locationIcons, isDesktop, siteTakeoverTheme?.logoRef])
  const title = siteTakeoverBannerContentPayload?.title ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.title) : undefined
  const subtitle = siteTakeoverBannerContentPayload?.subtitle ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.subtitle) : undefined
  const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
  const backgroundStyle = siteTakeoverBannerContentPayload?.backgroundStyle
  const paletteType = useLab ? (siteTakeoverBannerContentPayload?.palette ?? 'inverse') : customTakeover?.palette

  // Determine banner content based on config type
  // TODO: sub in campaign end date once available
  const baseBannerContent = useLab ? (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="horizontal" gap={8}>
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
          <Group direction="vertical" verticalAlign="start">
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText weight="bold" variant="medium">{title}</BodyText>
              <IconWrapper onClick={handleModalOpen}>
                <LineInfoCircleIcon size="XS" />
              </IconWrapper>
            </Group>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="small">{subtitle}</BodyText>
              {ctaLink && (
                <TextLink to={ctaLink} weight="bold" size="small">
                  {intl.formatMessage({
                    id: 'CKYzEV2OLCzWOzcY3HJI',
                    defaultMessage: 'View all',
                    description: 'CTA link text to view all takeover campaign offers on offer page banner',
                  })}
                </TextLink>
              )}
            </Group>
          </Group>
        </Group>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
          <BodyText variant="medium"><b>{title}</b><TitleSpacer />{subtitle}</BodyText>
          <IconWrapper onClick={handleModalOpen}>
            <LineInfoCircleIcon size="S" />
          </IconWrapper>
        </Group>
      </CSSBreakpoint>
    </>
  ) : (
    <BodyText variant="medium">
      {customTakeover?.offerPage?.bannerContent}
    </BodyText>
  )

  // Determine which background to use
  const renderBackground = () => {
    if (useCustom) {
      return <Background background="default" vertical={vertical} />
    }
    if (useLab && backgroundStyle) {
      return <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />
    }
    return null
  }

  return (
    <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
      <Banner ref={bannerRef} direction="horizontal" verticalAlign="center">
        {renderBackground()}
        <Content
          direction="horizontal"
          gap={12}
          verticalAlign="center"
          horizontalAlign="start"
          tabletHorizontalAlign="center"
          fullWidth
          className={getThemeClassName({ mobile: paletteType })}
        >
          {baseBannerContent}
        </Content>
      </Banner>
    </CSSBreakpoint>
  )
}

export default SiteTakeoverOfferPageBanner
